import { MAX_BAR_WIDTH, MAX_BAR_WITH_IMAGE_WIDTH } from '../constants';

const MAX_BAR_SPACING = 16; // Maximum spacing in pixels between bars and at the edges
const MAX_SPACING_RATIO = 0.08; // Maximum spacing ratio in percentage

interface BarSpacingParams {
  /** Total available width of the container in pixels */
  containerWidth: number;
  /** Number of bars to display */
  numBars: number;
  /** Whether the bars contain images (affects maximum bar width) */
  hasImages?: boolean;
}

/**
 * Calculates the spacing ratio between bars using d3's scaleBand model.
 * The ratio is a value between 0-1 that determines the space between bars.
 * For a given ratio R: space = barWidth * R
 *
 * The function aims to maintain consistent spacing by:
 * 1. Using 8% as the maximum spacing ratio for narrow bars
 * 2. Never exceeding the maximum spacing of 16px, for wide bars
 *
 * Note: The calculation doesn't need to be exact - small variations in spacing
 * (e.g. 15px vs 16px) won't be noticeable to users. The goal is to maintain
 * reasonable and consistent spacing while avoiding extremes.
 *
 * @returns Spacing ratio between 0-1 for d3's scaleBand paddingInner
 */
export const calculateBarSpacing = ({
  containerWidth,
  numBars,
  hasImages = false,
}: BarSpacingParams): number => {
  // Single bars don't need spacing
  if (numBars <= 1) return 0;

  // Get the per-bar width limit based on whether bars contain images
  const maxBarWidth = hasImages ? MAX_BAR_WITH_IMAGE_WIDTH : MAX_BAR_WIDTH;
  const maxBoundedWidth = Math.min(containerWidth, maxBarWidth * numBars);

  // Calculate the step from one bar to the next
  const step = maxBoundedWidth / numBars;

  // Calculate the spacing ratio that would give us 16px spacing
  // Since d3 uses the ratio relative to bar width (spacing = barWidth * ratio)
  // we divide our target spacing by the step
  return Math.min(MAX_SPACING_RATIO, MAX_BAR_SPACING / step);
};
